import { Navigate, Outlet, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import { decideProductionAccess } from "@/lib/coProducerAccess";
import { BackendUnreachable } from "./BackendUnreachable";

/**
 * Guard for the production-scoped routes (/productions/:productionId/*).
 *
 * Allows: admins, full producers (RLS still scopes them to briefings they own),
 * and co-producers — but a co-producer ONLY for the production they were granted
 * access to. Anyone else is redirected by the pure decision in coProducerAccess.
 */
export function ProductionRoute() {
  // Production-scoped routes use either :productionId (/productions/:id/*) or
  // :id (/briefings/:id/edit and friends) — both name the same briefing.
  const params = useParams();
  const productionId = params.productionId ?? params.id;
  const {
    session,
    isLoading,
    isRolesLoading,
    isSubscriptionLoading,
    isCoProducerLoading,
    isAdmin,
    hasProducerAccess,
    coProducerBriefingIds,
    isAuthDataUnavailable,
    refreshAuthData,
  } = useAuth();

  // Same rule as the overview layer: during an outage a paying producer must
  // not be told they have no access to their own production.
  const authDataUnavailable = isAuthDataUnavailable && !isAdmin;

  const decision = decideProductionAccess({
    isLoading: isLoading || isRolesLoading || isSubscriptionLoading || isCoProducerLoading,
    hasSession: !!session,
    isAdmin,
    hasProducerAccess,
    coProducerBriefingIds,
    productionId,
  });

  if (decision.type === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (authDataUnavailable) {
    return <BackendUnreachable onRetry={refreshAuthData} />;
  }

  if (decision.type === "redirect") {
    return <Navigate to={decision.to} replace />;
  }

  return <Outlet />;
}
